import {getDateRangeFromDateFilter} from 'nutshell-core/get-date-range-from-date-filter';
import {prettifyDateRange} from 'nutshell-core/utils';

import type {
    GetChatSettings_chatSettings as ChatSettings,
    GetChatbotReport_chatbotReport as ChatbotReport,
} from 'nutshell-graphql-types';

import {AiSummarySection} from './ai-summary-section';
import {ChatbotFeedback} from './chatbot-feedback';
import {ChatbotReportEmptyState, VARIANT} from './chatbot-report-empty-state';
import {ChatbotReportLoadingState} from './chatbot-report-loading-state';
import {ChatbotStatsGrid} from './chatbot-stats-grid';
import {KnowledgeGapsSection} from './knowledge-gaps-section';
import {LeaderboardsSection} from './leaderboards-section';
import {OddballThreadsSection} from './oddball-threads-section';
import {TopNudgesSection} from './top-nudges-section';

import styles from './chatbot-report-content.module.css';

interface Props {
    chatSettings: ChatSettings;
    report: ChatbotReport | null | undefined;
    isReportLoading: boolean;
    isAdjustingPeriod: boolean;
    period: string;
    hasError: boolean;
}

export function ChatbotReportContent(props: Props) {
    if (props.isReportLoading || props.isAdjustingPeriod) {
        return <ChatbotReportLoadingState />;
    }

    const {dbMetrics, aiMetrics} = props.report ?? {};
    if (!dbMetrics || !aiMetrics || dbMetrics.conversationCount === 0) {
        return (
            <ChatbotReportEmptyState variant={VARIANT.NoConversations} hasError={props.hasError} />
        );
    }

    const dateRangeObject = getDateRangeFromDateFilter(props.period);
    const dateRange = prettifyDateRange(dateRangeObject.startDate, dateRangeObject.endDate);

    return (
        <>
            <AiSummarySection aiMetrics={aiMetrics} dateRange={dateRange} />
            <ChatbotStatsGrid dbMetrics={dbMetrics} aiMetrics={aiMetrics} />
            <div className={styles.container}>
                <TopNudgesSection
                    topNudges={dbMetrics.topNudges}
                    secondaryColor={props.chatSettings.lookAndFeel.secondaryColor}
                />
                <ChatbotFeedback sentimentBreakdown={aiMetrics.sentimentBreakdown} />
            </div>
            <LeaderboardsSection
                commonTopics={aiMetrics.commonTopics}
                frustrationTopics={aiMetrics.frustrationTopics}
            />
            <KnowledgeGapsSection knowledgeGaps={aiMetrics.knowledgeGaps} />
            <OddballThreadsSection oddballThreads={aiMetrics.oddballThreads} />
        </>
    );
}
